<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left"><h1 class="page-title">{{ isEdit ? '編輯班表' : '新增班表' }}</h1></div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="loading || saving" @click="save(false)">
          {{ saving && savingAction === 'draft' ? '儲存中...' : saveButtonLabel }}
        </button>
        <button
          v-if="!isEdit"
          type="button"
          class="btn btn-success"
          :disabled="loading || saving"
          @click="save(true)"
        >
          {{ saving && savingAction === 'publish' ? '發布中...' : '儲存並發布' }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>載入中...</p></div>
      <form v-else class="form-container salon-form-page" @submit.prevent="save(false)">
        <div class="form-section">
          <h2 class="section-title">班表資料</h2>
          <p v-if="isPublished" class="form-hint">{{ publishedEditHint }}</p>
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label required-label">門市</label>
              <select
                v-model="form.storeId"
                class="form-control"
                :disabled="isEdit"
                required
                @change="handleStoreChange"
              >
                <option value="">請選擇</option>
                <option v-for="store in stores" :key="store.id" :value="store.id">{{ store.storeName }}</option>
              </select>
              <span v-if="!isEdit && !stores.length" class="form-hint">目前沒有開放美髮預約的門市。</span>
            </div>
            <div class="form-group">
              <label class="form-label required-label">設計師</label>
              <select
                v-model="form.stylistId"
                class="form-control"
                :disabled="isEdit || !form.storeId"
                required
                @change="handleStylistChange"
              >
                <option value="">請選擇</option>
                <option v-for="stylist in stylists" :key="stylist.id" :value="stylist.id">{{ stylist.displayName }}</option>
              </select>
              <span v-if="form.storeId && !stylists.length" class="form-hint">此門市目前沒有可服務的設計師。</span>
            </div>
            <div class="form-group">
              <label class="form-label">排班模式</label>
              <input :value="scheduleTypeLabel" class="form-control" disabled />
              <span class="form-hint">排班模式由門市「預約設定」統一管理。</span>
            </div>
            <div v-if="form.scheduleType === 'monthly'" class="form-group">
              <label class="form-label required-label">月份</label>
              <input
                v-model="form.yearMonth"
                type="month"
                class="form-control"
                :disabled="isEdit"
                required
                @change="handleMonthChange"
              />
            </div>
            <div
              v-if="form.scheduleType === 'weekly' && isEdit && isPublished"
              class="form-group"
            >
              <label class="form-label">修改前生效日期</label>
              <input
                :value="originalEffectiveFrom"
                type="date"
                class="form-control"
                disabled
              />
              <span class="form-hint">目前已發布週班表的生效日期。</span>
            </div>
            <div v-if="form.scheduleType === 'weekly'" class="form-group">
              <label class="form-label required-label">
                {{ isEdit && isPublished ? '修改後生效日期' : '週班表生效日期' }}
              </label>
              <input
                v-model="form.effectiveFrom"
                type="date"
                class="form-control"
                :disabled="isEdit && !isPublished"
                :min="weeklyEffectiveMin"
                required
                @change="loadReservationLocks"
              />
              <span class="form-hint">
                {{ isEdit && isPublished
                  ? '請指定新版週班表的未來生效日期；生效前仍使用目前班表。'
                  : '每位設計師同一日期只會有一個已發布週班表生效。' }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="form.stylistId && scheduleDays.length" class="form-section">
          <div class="section-heading">
            <div>
              <h2 class="section-title">{{ form.scheduleType === 'monthly' ? '整月班表' : '整週班表' }}</h2>
              <p class="form-hint">
                {{ form.scheduleType === 'monthly'
                  ? '請逐日勾選設計師有上班的門市營業時段。'
                  : '請勾選設計師有上班的門市營業時段。' }}
              </p>
            </div>
            <div class="schedule-actions">
              <label class="schedule-option">
                <input
                  type="checkbox"
                  :checked="allShiftsSelected"
                  :disabled="!hasScheduleOptions"
                  @change="toggleAllShifts($event.target.checked)"
                />
                <span>全選班次</span>
              </label>
              <label v-if="!isEdit && otherStylists.length" class="schedule-option">
                <input
                  v-model="applyToOtherStylists"
                  type="checkbox"
                  @change="handleApplyToOtherStylistsChange"
                />
                <span>套用到同門市其他設計師</span>
              </label>
            </div>
          </div>
          <div v-if="applyToOtherStylists" class="stylist-selection">
            <div class="stylist-selection-heading">
              <strong>選擇要套用的設計師</strong>
              <label class="schedule-option">
                <input
                  type="checkbox"
                  :checked="allOtherStylistsSelected"
                  @change="toggleAllOtherStylists($event.target.checked)"
                />
                <span>全部其他設計師</span>
              </label>
            </div>
            <div class="stylist-options">
              <label
                v-for="stylist in otherStylists"
                :key="stylist.id"
                class="stylist-option"
                :class="{ selected: targetStylistIds.includes(stylist.id) }"
              >
                <input v-model="targetStylistIds" type="checkbox" :value="stylist.id" />
                <span>{{ stylist.displayName }}</span>
              </label>
            </div>
          </div>
          <div class="schedule-days">
            <div
              v-for="day in scheduleDays"
              :key="day.date"
              class="schedule-day calendar-day"
            >
              <div
                class="schedule-day-heading"
                :class="{
                  'is-sunday': day.dayOfWeek === 0,
                  'is-saturday': day.dayOfWeek === 6
                }"
              >
                <div>
                  <strong>{{ form.scheduleType === 'monthly' ? day.date : day.label }}</strong>
                  <span v-if="form.scheduleType === 'monthly'">{{ day.label }}</span>
                </div>
              </div>
              <div v-if="day.shifts.length" class="schedule-shifts">
                <label
                  v-for="(shift, shiftIndex) in day.shifts"
                  :key="shiftIndex"
                  class="schedule-shift-option"
                  :class="{ selected: shift.selected, locked: shift.locked }"
                  :title="reservationLockTitle(shift)"
                >
                  <input
                    v-model="shift.selected"
                    type="checkbox"
                    :disabled="shift.locked"
                  />
                  <span>{{ shift.startTime }}－{{ shift.endTime }}</span>
                  <small v-if="shift.locked" class="reservation-lock-label">
                    已有 {{ shift.lockedReservations.length }} 筆預約
                  </small>
                </label>
              </div>
              <p v-else class="closed-day">門市本日未營業</p>
            </div>
          </div>
        </div>
        <div v-else-if="form.stylistId && form.storeId && form.scheduleType" class="empty-state">
          {{ form.scheduleType === 'monthly' ? '請選擇要建立班表的月份。' : '目前沒有可設定的星期。' }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { salonService } from '@/api/modules/salon.js';
import { storeService } from '@/api/modules/stores.js';
import { salonDateOnly } from '@/utils/salonDate.js';
import { fireSwalDeferred, showError, showSuccess } from '@/utils/swal.js';

const emptyForm = () => ({
  storeId: '', stylistId: '', scheduleType: '', yearMonth: '',
  effectiveFrom: '', effectiveTo: ''
});
const weekdayLabels = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
const parseDate = value => new Date(`${value}T00:00:00Z`);
const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
}[character]));
const parseErrorData = value => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

export default {
  name: 'SalonScheduleForm',
  data() {
    return {
      loading: false, saving: false, savingAction: null,
      isPublished: false, originalEffectiveFrom: '', stores: [], stylists: [],
      form: emptyForm(), storeSettings: null, storeHours: [], scheduleDays: [],
      applyToOtherStylists: false, targetStylistIds: [], reservationLocks: [],
      reservationLocksRequestId: 0
    };
  },
  computed: {
    isEdit() { return Boolean(this.$route.params.id); },
    publishedEditHint() {
      return this.form.scheduleType === 'monthly'
        ? '月班表會直接更新；若影響既有預約，系統將拒絕修改並列出衝突資料。'
        : '修改已發布週班表時，系統會建立新的草稿版本。';
    },
    saveButtonLabel() {
      if (!this.isEdit) return '儲存草稿';
      if (this.isPublished && this.form.scheduleType === 'monthly') return '儲存修改';
      if (this.isPublished) return '建立新版草稿';
      return '儲存草稿';
    },
    scheduleTypeLabel() {
      return { monthly: '月排班', weekly: '週排班' }[this.form.scheduleType] || '請先選擇門市';
    },
    today() {
      return salonDateOnly(new Date());
    },
    tomorrow() {
      const date = new Date(`${this.today}T00:00:00Z`);
      date.setUTCDate(date.getUTCDate() + 1);
      return date.toISOString().slice(0, 10);
    },
    weeklyEffectiveMin() {
      return this.isEdit && this.isPublished ? this.tomorrow : this.today;
    },
    scheduleOptions() {
      return this.scheduleDays.flatMap(day => day.shifts);
    },
    hasScheduleOptions() {
      return this.scheduleOptions.length > 0;
    },
    allShiftsSelected() {
      return this.hasScheduleOptions && this.scheduleOptions.every(shift => shift.selected);
    },
    otherStylists() {
      return this.stylists.filter(stylist => stylist.id !== this.form.stylistId);
    },
    allOtherStylistsSelected() {
      return this.otherStylists.length > 0
        && this.otherStylists.every(stylist => this.targetStylistIds.includes(stylist.id));
    }
  },
  async mounted() {
    this.loading = true;
    try {
      const [stores, settings] = await Promise.all([
        storeService.getStores({ page: 1, pageSize: 100, status: 'active' }),
        salonService.listSettings()
      ]);
      const activeStores = stores?.data?.items || [];
      const openStoreIds = new Set(
        (settings?.data || [])
          .filter(item => item.bookingEnabled)
          .map(item => item.storeId)
      );
      this.stores = this.isEdit
        ? activeStores
        : activeStores.filter(store => openStoreIds.has(store.id));
      if (this.isEdit) {
        await this.loadSchedule();
      } else if (this.$route.query.storeId
        && this.stores.some(store => store.id === this.$route.query.storeId)) {
        this.form.storeId = this.$route.query.storeId;
        await this.handleStoreChange();
      }
    } catch (error) {
      await this.handleError(error, '班表資料載入失敗');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadSchedule() {
      const response = await salonService.getSchedule(this.$route.params.id);
      const item = response.data;
      this.isPublished = item.status === 'published';
      this.originalEffectiveFrom = this.dateOnly(item.effectiveFrom);
      this.form = {
        storeId: item.storeId,
        stylistId: item.stylistId,
        scheduleType: item.scheduleType,
        yearMonth: item.yearMonth || '',
        effectiveFrom: item.status === 'published' && item.scheduleType === 'weekly'
          ? ''
          : this.dateOnly(item.effectiveFrom),
        effectiveTo: this.dateOnly(item.effectiveTo)
      };
      await this.loadStoreContext(this.form.storeId, true);
      this.buildScheduleDays(item.shifts || []);
      if (item.scheduleType === 'monthly' || item.status !== 'published') {
        await this.loadReservationLocks();
      }
    },
    async loadStylists(storeId) {
      if (!storeId) {
        this.stylists = [];
        return;
      }
      const response = await salonService.listStylists({
        page: 1, pageSize: 100, status: 'active', storeId
      });
      if (this.form.storeId !== storeId) return;
      this.stylists = response?.data?.items || [];
    },
    async loadStoreContext(storeId, preserveMode = false) {
      if (!storeId) {
        this.stylists = [];
        this.storeHours = [];
        return;
      }
      const [settingsResponse, hoursResponse] = await Promise.all([
        salonService.getSettings(storeId),
        storeService.getStoreHours(storeId),
        this.loadStylists(storeId)
      ]);
      if (this.form.storeId !== storeId) return;
      const settings = settingsResponse?.data || {};
      this.storeSettings = settings;
      if (!preserveMode) {
        const today = salonDateOnly(new Date());
        this.form.scheduleType = this.modeForDate(today) || '';
        if (this.form.scheduleType === 'weekly') {
          this.form.effectiveFrom = today;
          this.form.effectiveTo = '';
        }
      }
      this.storeHours = hoursResponse?.data?.hours || [];
    },
    async handleStoreChange() {
      this.form.stylistId = '';
      this.form.scheduleType = '';
      this.form.yearMonth = '';
      this.form.effectiveFrom = '';
      this.form.effectiveTo = '';
      this.applyToOtherStylists = false;
      this.targetStylistIds = [];
      this.scheduleDays = [];
      try {
        await this.loadStoreContext(this.form.storeId);
      } catch (error) {
        await this.handleError(error, '門市排班設定載入失敗');
      }
    },
    handleStylistChange() {
      this.scheduleDays = [];
      this.applyToOtherStylists = false;
      this.targetStylistIds = [];
      if (!this.form.stylistId) return;
      if (this.form.scheduleType === 'weekly' || this.form.yearMonth) {
        this.buildScheduleDays();
      }
    },
    modeForDate(dateValue) {
      if (!this.storeSettings) return '';
      if (this.storeSettings.pendingScheduleMode
        && this.storeSettings.scheduleModeEffectiveFrom
        && dateValue >= this.dateOnly(this.storeSettings.scheduleModeEffectiveFrom)) {
        return this.storeSettings.pendingScheduleMode;
      }
      return this.storeSettings.scheduleMode;
    },
    handleMonthChange() {
      if (!this.form.yearMonth) {
        this.scheduleDays = [];
        return;
      }
      if (!this.form.stylistId) return;
      this.buildScheduleDays();
    },
    businessSlots(dayOfWeek) {
      const hours = this.storeHours.find(item => Number(item.dayOfWeek) === dayOfWeek);
      if (!hours || !hours.isOpen) return [];
      if (Array.isArray(hours.timeSlots) && hours.timeSlots.length) {
        return hours.timeSlots
          .filter(slot => slot?.openTime && slot?.closeTime)
          .map(slot => ({
            startTime: this.shortTime(slot.openTime),
            endTime: this.shortTime(slot.closeTime)
          }));
      }
      return hours.openTime && hours.closeTime
        ? [{ startTime: this.shortTime(hours.openTime), endTime: this.shortTime(hours.closeTime) }]
        : [];
    },
    periodDates() {
      if (this.form.scheduleType === 'monthly' && /^\d{4}-\d{2}$/.test(this.form.yearMonth)) {
        const [year, month] = this.form.yearMonth.split('-').map(Number);
        const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
        return Array.from({ length: lastDay }, (_, index) => (
          `${this.form.yearMonth}-${String(index + 1).padStart(2, '0')}`
        ));
      }
      return [];
    },
    buildScheduleDays(existingShifts = null) {
      const grouped = new Map();
      if (existingShifts) {
        existingShifts.forEach(shift => {
          const key = this.form.scheduleType === 'monthly'
            ? this.dateOnly(shift.shiftDate)
            : Number(shift.dayOfWeek);
          const shifts = grouped.get(key) || [];
          shifts.push({
            startTime: this.shortTime(shift.startTime),
            endTime: this.shortTime(shift.endTime),
            selected: true,
            locked: false,
            lockedReservations: []
          });
          grouped.set(key, shifts);
        });
      }
      const days = this.form.scheduleType === 'weekly'
        ? [1, 2, 3, 4, 5, 6, 0].map(dayOfWeek => ({ date: `weekday-${dayOfWeek}`, dayOfWeek }))
        : this.periodDates().map(date => ({ date, dayOfWeek: parseDate(date).getUTCDay() }));
      this.scheduleDays = days.map(({ date, dayOfWeek }) => {
        const key = this.form.scheduleType === 'monthly' ? date : dayOfWeek;
        const configuredShifts = existingShifts ? (grouped.get(key) || []) : [];
        let shifts;
        shifts = this.businessSlots(dayOfWeek).map(slot => ({
          ...slot,
          selected: configuredShifts.some(existing => (
            existing.startTime === slot.startTime && existing.endTime === slot.endTime
          )),
          locked: false,
          lockedReservations: []
        }));
        configuredShifts.forEach(existing => {
          if (!shifts.some(slot => (
            slot.startTime === existing.startTime && slot.endTime === existing.endTime
          ))) {
            shifts.push(existing);
          }
        });
        return {
          date,
          dayOfWeek,
          label: weekdayLabels[dayOfWeek],
          shifts
        };
      });
      this.applyReservationLocks();
    },
    async loadReservationLocks() {
      this.reservationLocks = [];
      this.applyReservationLocks();
      if (!this.isEdit) return;
      if (this.form.scheduleType === 'weekly' && !this.form.effectiveFrom) return;
      const requestId = ++this.reservationLocksRequestId;
      try {
        const response = await salonService.getScheduleReservationLocks(
          this.$route.params.id,
          {
            effectiveFrom: this.form.scheduleType === 'weekly'
              ? this.form.effectiveFrom
              : null,
            effectiveTo: this.form.scheduleType === 'weekly'
              ? (this.form.effectiveTo || null)
              : null
          }
        );
        if (requestId !== this.reservationLocksRequestId) return;
        this.reservationLocks = response?.data || [];
        this.applyReservationLocks();
      } catch (error) {
        if (requestId !== this.reservationLocksRequestId) return;
        await this.handleError(error, '已有預約班次載入失敗');
      }
    },
    applyReservationLocks() {
      this.scheduleOptions.forEach(shift => {
        shift.locked = false;
        shift.lockedReservations = [];
      });
      this.reservationLocks.forEach(reservation => {
        const date = this.dateOnly(reservation.appointmentDate);
        const dayOfWeek = parseDate(date).getUTCDay();
        const day = this.scheduleDays.find(item => (
          this.form.scheduleType === 'monthly'
            ? item.date === date
            : item.dayOfWeek === dayOfWeek
        ));
        if (!day) return;
        const startTime = this.shortTime(reservation.startTime);
        const endTime = this.shortTime(reservation.endTime);
        const shift = day.shifts.find(item => (
          item.startTime <= startTime && item.endTime >= endTime
        ));
        if (!shift) return;
        shift.locked = true;
        shift.selected = true;
        shift.lockedReservations.push(reservation);
      });
    },
    reservationLockTitle(shift) {
      if (!shift.locked) return '';
      return shift.lockedReservations.map(item => (
        `${this.dateOnly(item.appointmentDate)} ${this.shortTime(item.startTime)}`
        + ` ${item.contactName || item.reservationNumber || '已有預約'}`
      )).join('\n');
    },
    toggleAllShifts(selected) {
      this.scheduleOptions.forEach(shift => {
        if (!shift.locked) shift.selected = selected;
      });
    },
    handleApplyToOtherStylistsChange() {
      if (!this.applyToOtherStylists) this.targetStylistIds = [];
    },
    toggleAllOtherStylists(selected) {
      this.targetStylistIds = selected
        ? this.otherStylists.map(stylist => stylist.id)
        : [];
    },
    scheduleShifts() {
      const monthly = this.form.scheduleType === 'monthly';
      return this.scheduleDays.flatMap(day => day.shifts
        .filter(shift => shift.selected)
        .map(shift => ({
        ...(monthly ? { shiftDate: day.date } : { dayOfWeek: day.dayOfWeek }),
        startTime: shift.startTime,
        endTime: shift.endTime
        })));
    },
    async save(publishImmediately = false) {
      if (this.saving) return;
      if (!this.form.scheduleType || !this.scheduleDays.length) {
        await showError('資料不足', '請先選擇門市及排班月份或週別。');
        return;
      }
      if (this.form.scheduleType === 'weekly' && !this.form.effectiveFrom) {
        await showError('資料不足', '請設定週班表生效日期。');
        return;
      }
      if (this.form.scheduleType === 'weekly' && this.isEdit && this.isPublished
        && this.form.effectiveFrom <= this.today) {
        await showError('生效日期錯誤', '修改已發布週班表必須指定未來生效日期。');
        return;
      }
      if (this.applyToOtherStylists && !this.targetStylistIds.length) {
        await showError('資料不足', '請至少選擇一位要套用班表的其他設計師。');
        return;
      }
      this.saving = true;
      this.savingAction = publishImmediately ? 'publish' : 'draft';
      try {
        const monthly = this.form.scheduleType === 'monthly';
        const body = {
          storeId: this.form.storeId,
          stylistId: this.form.stylistId,
          scheduleType: this.form.scheduleType,
          yearMonth: monthly ? this.form.yearMonth : null,
          effectiveFrom: monthly ? null : this.form.effectiveFrom,
          effectiveTo: monthly ? null : (this.form.effectiveTo || null),
          ...(!this.isEdit ? { publishImmediately } : {}),
          shifts: this.scheduleShifts()
        };
        let savedCount = 1;
        if (this.isEdit) {
          await salonService.updateSchedule(this.$route.params.id, body);
        } else {
          const stylistIds = this.applyToOtherStylists
            ? [this.form.stylistId, ...this.targetStylistIds]
            : [this.form.stylistId];
          if (stylistIds.length > 1) {
            await salonService.createSchedules({
              ...body,
              stylistIds,
              stylistId: undefined
            });
          } else {
            await salonService.createSchedule(body);
          }
          savedCount = stylistIds.length;
        }
        let successMessage = '班表草稿已儲存';
        if (publishImmediately) {
          successMessage = savedCount > 1
            ? `已為 ${savedCount} 位設計師建立並發布班表`
            : '班表已儲存並發布';
        } else if (this.isEdit && this.isPublished && monthly) {
          successMessage = '月班表已更新';
        } else if (this.isEdit && this.isPublished) {
          successMessage = '新版班表草稿已建立';
        } else if (!this.isEdit && savedCount > 1) {
          successMessage = `已為 ${savedCount} 位設計師建立班表草稿`;
        }
        await showSuccess(this.isEdit ? '更新成功' : '建立成功', successMessage);
        this.goBack();
      } catch (error) {
        const errorData = parseErrorData(error.response?.data);
        if (errorData?.error?.code === 'SCHEDULE_CONFLICT') {
          await this.showScheduleConflicts(errorData.error.details || {});
        } else {
          await this.handleError(error, publishImmediately ? '班表儲存並發布失敗' : '班表儲存失敗');
        }
      } finally {
        this.saving = false;
        this.savingAction = null;
      }
    },
    dateOnly(value) { return salonDateOnly(value); },
    shortTime(value) { return value ? String(value).slice(0, 5) : ''; },
    async showScheduleConflicts(details) {
      const reservations = details.reservationConflicts || [];
      const crossStore = details.crossStoreConflicts || [];
      const reservationRows = reservations.map(item => `
        <tr>
          <td>${escapeHtml(item.reservationNumber || '-')}</td>
          <td>${escapeHtml(item.contactName || '未填姓名')}</td>
          <td>${escapeHtml(item.contactPhone || '-')}</td>
          <td>${escapeHtml(this.dateOnly(item.appointmentDate))}</td>
          <td>${escapeHtml(this.shortTime(item.startTime))}－${escapeHtml(this.shortTime(item.endTime))}</td>
        </tr>
      `).join('');
      const reservationTable = reservations.length
        ? `<h4>受影響預約（${reservations.length} 筆）</h4>
           <div style="overflow-x:auto">
             <table style="width:100%;border-collapse:collapse;text-align:left">
               <thead><tr><th>預約編號</th><th>客人</th><th>電話</th><th>日期</th><th>時間</th></tr></thead>
               <tbody>${reservationRows}</tbody>
             </table>
           </div>`
        : '';
      const crossStoreMessage = crossStore.length
        ? `<p style="margin-top:14px">另有 ${crossStore.length} 個跨門市班次重疊，請先調整。</p>`
        : '';
      await fireSwalDeferred({
        icon: 'error',
        title: '班表無法儲存',
        html: `<p>新班表會影響既有預約，請先完成人工改期、改派或取消。</p>
          ${reservationTable}${crossStoreMessage}`,
        width: 900,
        confirmButtonText: '我知道了',
        confirmButtonColor: '#dc3545'
      });
    },
    goBack() {
      if (this.$route.query.returnToStore === '1' && this.form.storeId) {
        this.$router.push({
          name: 'SalonStoreSchedules',
          params: { storeId: this.form.storeId }
        });
        return;
      }
      this.$router.push({ name: 'SalonSchedules' });
    },
    async handleError(error, fallback) {
      await showError(fallback, error.response?.data?.message || error.message || fallback);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_form-common.scss';
.salon-form-page { max-width: 1100px; margin: 0 auto; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.section-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px; }
.section-heading .section-title { margin-bottom: 0; }
.schedule-days { display: grid; gap: 12px; }
.schedule-actions { align-items: flex-end; display: flex; flex-direction: column; gap: 8px; }
.schedule-option { align-items: center; cursor: pointer; display: flex; gap: 8px; margin: 0; }
.schedule-option input { cursor: pointer; }
.stylist-selection {
  background: #f8fafc;
  border: 1px solid #dbe3ec;
  border-radius: 8px;
  margin-bottom: 14px;
  padding: 14px;
}
.stylist-selection-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.stylist-options { display: flex; flex-wrap: wrap; gap: 10px; }
.stylist-option {
  align-items: center;
  background: #fff;
  border: 1px solid #dbe3ec;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  margin: 0;
  padding: 8px 12px;
}
.stylist-option.selected { background: #eff6ff; border-color: #3b82f6; }
.schedule-day { border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; }
.schedule-day-heading { align-items: center; display: flex; justify-content: space-between; gap: 12px; }
.schedule-day-heading div { align-items: baseline; display: flex; gap: 10px; }
.schedule-day-heading span, .closed-day { color: #64748b; }
.calendar-day {
  align-items: stretch;
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 0;
  overflow: hidden;
  padding: 0;
}
.calendar-day .schedule-day-heading {
  background: #f1f5f9;
  border-right: 1px solid #cbd5e1;
  color: #111827;
  justify-content: flex-start;
  padding: 14px;
}
.calendar-day .schedule-day-heading div {
  align-items: flex-start;
  flex-direction: column;
  gap: 2px;
}
.calendar-day .schedule-day-heading span { color: #111827; }
.calendar-day .schedule-shifts,
.calendar-day .closed-day {
  align-content: center;
  margin: 0;
  padding: 14px;
}
.calendar-day .is-sunday { background: #fecaca; color: #111827; }
.calendar-day .is-saturday { background: #bbf7d0; color: #111827; }
.schedule-shifts { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.schedule-shift-option {
  align-items: center;
  border: 1px solid #dbe3ec;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  padding: 9px 12px;
}
.schedule-shift-option.selected {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1d4ed8;
}
.schedule-shift-option.locked {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #78350f;
  cursor: not-allowed;
}
.reservation-lock-label { font-weight: 600; }
.schedule-shift-option input { cursor: pointer; }
.schedule-shift-option.locked input { cursor: not-allowed; }
.closed-day { margin: 10px 0 0; }
@media (max-width: 768px) {
  .form-grid { grid-template-columns: 1fr; }
  .section-heading { align-items: flex-start; flex-direction: column; }
  .schedule-actions { align-items: flex-start; }
  .stylist-selection-heading { align-items: flex-start; flex-direction: column; }
  .calendar-day { grid-template-columns: 105px minmax(0, 1fr); }
}
</style>
